import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { useTheme } from 'server/theme/hooks';
import { DropdownWrapper, MenuDropdownBridge, MenuDropdownStyled, NavigationOverlay } from './Styles';

// Trigger components must have this className for click and keyboard event handling to work
export const DROPDOWN_TRIGGER_CLASS = 'dropdown-trigger';

const isTriggerElement = (element) => {
  if (!element) return false;
  if (element.classList && element.classList.contains(DROPDOWN_TRIGGER_CLASS)) {
    return true;
  }
  return element.closest && element.closest(`.${DROPDOWN_TRIGGER_CLASS}`) !== null;
};

function MenuDropdown({
  isOpen,
  trigger,
  children,
  role = 'menu',
  ariaHidden,
  onClose,
  onOpen,
}) {
  const theme = useTheme();
  const isMobileOS = useSelector(state => state.common.isMobileOS, shallowEqual);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const focusOnOpenRef = useRef(null); // 'first' or 'last'

  const getMenuItems = () => {
    const dropdownElement = dropdownRef.current;
    if (!dropdownElement) return [];
    const items = dropdownElement.querySelectorAll('[role="menuitem"]');
    return Array.from(items).filter(item => item.offsetParent !== null && !item.disabled);
  };

  // Show dropdown before opening, hide after closing animation ends
  // To enable/disable tab navigation
  useEffect(() => {
    const dropdownElement = dropdownRef.current;
    if (!dropdownElement) return undefined;

    const setDropdownVisibility = (visible) => {
      dropdownElement.style.visibility = visible ? 'visible' : 'hidden';
      dropdownElement.previousElementSibling.style['z-index'] = visible ? theme.zIndex.navigationMenuIcon : '';
    };

    if (isOpen) {
      setDropdownVisibility(true);

      // Handle focus on open
      if (focusOnOpenRef.current) {
        const focusTarget = focusOnOpenRef.current;
        focusOnOpenRef.current = null;
        const focusableItems = getMenuItems();
        if (focusableItems.length > 0) {
          const index = focusTarget === 'first' ? 0 : focusableItems.length - 1;
          focusableItems[index].focus();
        }
      }
    } else {
      let fallbackTimer;

      const hide = () => {
        clearTimeout(fallbackTimer);
        setDropdownVisibility(false);
      };

      const handleTransitionEnd = (event) => {
        if (event.target === dropdownElement) {
          hide();
        }
      };

      // Fallback: if transitionend never fires (e.g. the opening transition was
      // interrupted before it started), hide after transition total time + 100ms buffer.
      // CSS transition: 200ms delay + 200ms duration = 400ms total.
      fallbackTimer = setTimeout(hide, 500);

      dropdownElement.addEventListener('transitionend', handleTransitionEnd);

      return () => {
        clearTimeout(fallbackTimer);
        dropdownElement.removeEventListener('transitionend', handleTransitionEnd);
      };
    }
    return undefined;
  }, [isOpen, theme.zIndex.navigationMenuIcon]);

  useEffect(() => {
    if (isOpen && !isMobileOS) {
      const onGlobalKeyDown = (event) => {
        if (event.key === 'Escape' || event.key === 'Esc') {
          onClose?.();
        }
      };
      window.addEventListener('keydown', onGlobalKeyDown);
      return () => window.removeEventListener('keydown', onGlobalKeyDown);
    }
  }, [isOpen, isMobileOS, onClose]);

  const handleMouseEnter = () => {
    if (!isMobileOS) {
      onOpen?.();
    }
  };

  const handleMouseLeave = () => {
    if (!isMobileOS) {
      onClose?.();
    }
  };

  const handleTriggerKeyDown = (event) => {
    const { key } = event;
    if (key === 'ArrowDown' || key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (!isOpen) {
        focusOnOpenRef.current = 'first';
        onOpen?.();
      } else if (key === 'ArrowDown') {
        const items = getMenuItems();
        if (items.length > 0) items[0].focus();
      } else {
        onClose?.();
      }
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        focusOnOpenRef.current = 'last';
        onOpen?.();
      } else {
        const items = getMenuItems();
        if (items.length > 0) items[items.length - 1].focus();
      }
    }
  };

  const handleDisplayKeyDown = (event) => {
    const { key } = event;
    if (key === 'ArrowDown') {
      event.preventDefault();
      const items = getMenuItems();
      const currentIndex = items.indexOf(event.target);
      if (currentIndex !== -1) {
        const nextIndex = (currentIndex + 1) % items.length;
        items[nextIndex].focus();
      }
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      const items = getMenuItems();
      const currentIndex = items.indexOf(event.target);
      if (currentIndex !== -1) {
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        items[prevIndex].focus();
      }
    } else if (key === 'Home') {
      event.preventDefault();
      const items = getMenuItems();
      if (items.length > 0) items[0].focus();
    } else if (key === 'End') {
      event.preventDefault();
      const items = getMenuItems();
      if (items.length > 0) items[items.length - 1].focus();
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.target.click();
    } else if (key === 'Escape') {
      event.preventDefault();
      onClose?.();
      if (containerRef.current) {
        const trigger = containerRef.current.querySelector(`.${DROPDOWN_TRIGGER_CLASS}`);
        if (trigger) trigger.focus();
      }
    }
  };

  const handleKeyDown = (event) => {
    if (isMobileOS) {
      return;
    }

    const isTrigger = isTriggerElement(event.target);
    const dropdownElement = dropdownRef.current;

    if (isTrigger) {
      handleTriggerKeyDown(event);
    } else if (isOpen && dropdownElement && dropdownElement.contains(event.target)) {
      handleDisplayKeyDown(event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose?.();
      if (containerRef.current) {
        const trigger = containerRef.current.querySelector(`.${DROPDOWN_TRIGGER_CLASS}`);
        if (trigger) trigger.focus();
      }
    }
  };

  const handleClick = (event) => {
    if (isMobileOS) {
      const isTrigger = isTriggerElement(event.target);

      if (isTrigger) {
        if (isOpen) {
          onClose?.();
        } else {
          onOpen?.();
        }
      }
    }
  };

  const handleOverlayClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClose?.();
  };

  const handleOverlayTouchStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOverlayTouchEnd = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClose?.();
  };

  const handleBlur = () => {
    // Use setTimeout so document.activeElement has settled to the new element
    // before we check if focus has left the container. Relying on event.relatedTarget
    // is unreliable across browsers when focus moves via programmatic .focus() calls.
    if (!isMobileOS && isOpen) {
      setTimeout(() => {
        if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
          onClose?.();
        }
      }, 0);
    }
  };

  return (
    <>
      {isMobileOS && (
        <NavigationOverlay
          isVisible={isOpen}
          transparent
          onClick={handleOverlayClick}
          onTouchStart={handleOverlayTouchStart}
          onTouchEnd={handleOverlayTouchEnd}
        />
      )}
      <DropdownWrapper
        innerRef={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onBlur={handleBlur}
        suppressHydrationWarning
      >
        {trigger}
        <MenuDropdownStyled
          innerRef={dropdownRef}
          isOpen={isOpen}
          role={role}
          aria-hidden={typeof ariaHidden === 'boolean' ? ariaHidden : !isOpen}
          hidden={!isOpen || undefined}
        >
          <MenuDropdownBridge/>
          {children}
        </MenuDropdownStyled>
      </DropdownWrapper>
    </>
  );
}

MenuDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  trigger: PropTypes.node,
  children: PropTypes.node.isRequired,
  role: PropTypes.string,
  ariaHidden: PropTypes.bool,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
};

export default MenuDropdown;

